import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    linkedSignal,
    output,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { AlertService } from "../../../services/alert.service";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { CatalogCard, CatalogTrack, TrailApi } from "./trail.api";
import { TrailLabels } from "./trail.labels";

type FilterMode = "available" | "pathways";

@TypeManagerDecorator("s25-ng-trail-catalog")
@Component({
    selector: "s25-ng-trail-catalog",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25LoadingInlineStaticComponent],
    template: `
        <section class="trail-catalog">
            <header class="trail-catalog__header">
                <h2 class="trail-catalog__title">{{ labels.BROWSE_CATALOG }}</h2>
                @if (cohortLabels().length) {
                    <div class="trail-catalog__cohorts">
                        @for (cohortLabel of cohortLabels(); track cohortLabel) {
                            <span class="trail-catalog__cohort-badge">{{ cohortLabel }}</span>
                        }
                    </div>
                }
            </header>

            <div class="trail-catalog__filters">
                <div class="trail-catalog__filter-toggles">
                    <button
                        type="button"
                        data-id="filter-available"
                        [class.active]="filterMode() === 'available'"
                        (click)="setFilterMode('available')"
                        class="trail-catalog__filter-btn"
                    >
                        {{ labels.AVAILABLE_TO_ME }}
                    </button>
                    <button
                        type="button"
                        data-id="filter-pathways"
                        [class.active]="filterMode() === 'pathways'"
                        (click)="setFilterMode('pathways')"
                        class="trail-catalog__filter-btn"
                    >
                        {{ labels.MY_PATHWAYS }}
                    </button>
                </div>
                <input
                    type="text"
                    class="trail-catalog__search"
                    placeholder="Search pathways…"
                    [attr.aria-label]="'Search pathways'"
                    [ngModel]="searchQuery()"
                    (ngModelChange)="searchQuery.set($event)"
                />
            </div>

            @if (catalogRes.isLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading catalog…'" />
            } @else if (catalogRes.error()) {
                <p class="trail-catalog__error">Unable to load catalog.</p>
            } @else {
                <div class="trail-catalog__grid">
                    @for (card of filteredCards(); track card.defnId) {
                        <article
                            class="trail-catalog__card"
                            [class.trail-catalog__card--unavailable]="!card.availableToViewer"
                            (click)="selectCard(card)"
                        >
                            <div class="trail-catalog__card-header">
                                <h3 class="trail-catalog__card-name">{{ card.displayName }}</h3>
                                <div class="trail-catalog__card-badges">
                                    @if (card.viewerHasDeclaration) {
                                        <span class="trail-catalog__declared-badge" data-id="declared-badge">{{
                                            labels.DECLARED
                                        }}</span>
                                    }
                                    @if (card.viewerHasAward) {
                                        <span class="trail-catalog__awarded-badge" data-id="awarded-badge">{{
                                            labels.AWARDED
                                        }}</span>
                                    }
                                </div>
                            </div>
                            @if (card.endingAwardDisplayName) {
                                <span class="trail-catalog__award-label">{{ card.endingAwardDisplayName }}</span>
                            }
                            @if (card.description) {
                                <p class="trail-catalog__card-description">{{ card.description }}</p>
                            }
                            @if (card.tracks?.length) {
                                <ul class="trail-catalog__tracks" (click)="$event.stopPropagation()">
                                    @for (track of card.tracks; track track.trackId) {
                                        <li class="trail-catalog__track">
                                            <label class="trail-catalog__track-label">
                                                <input
                                                    type="checkbox"
                                                    [attr.data-id]="'track-' + track.trackId"
                                                    [checked]="isTrackSelected(card, track.trackId)"
                                                    [disabled]="isTrackDisabled(card, track)"
                                                    (change)="toggleTrack(card, track.trackId)"
                                                />
                                                <span class="trail-catalog__track-name">{{ track.name }}</span>
                                            </label>
                                            @if (track.decoration) {
                                                <p class="trail-catalog__track-decoration">{{ track.decoration }}</p>
                                            }
                                            @if (track.cannotCombineWith?.length) {
                                                <p class="trail-catalog__track-exclusivity">
                                                    {{ labels.CANNOT_COMBINE_WITH }}:
                                                    {{ cannotCombineWithLabel(track) }}
                                                </p>
                                            }
                                        </li>
                                    }
                                </ul>
                            }
                            @if (!card.availableToViewer && card.ineligibilityDescriptions?.length) {
                                <ul class="trail-catalog__ineligibility">
                                    @for (reason of card.ineligibilityDescriptions; track reason) {
                                        <li>{{ reason }}</li>
                                    }
                                </ul>
                            }
                            <div class="trail-catalog__actions" (click)="$event.stopPropagation()">
                                @if (!card.viewerHasDeclaration) {
                                    <button
                                        type="button"
                                        class="trail-catalog__action-btn"
                                        data-id="declare-btn"
                                        [disabled]="!card.availableToViewer"
                                        (click)="declarePathway(card)"
                                    >
                                        {{ labels.DECLARE }}
                                    </button>
                                } @else {
                                    @if (card.tracks?.length) {
                                        <button
                                            type="button"
                                            class="trail-catalog__action-btn"
                                            data-id="save-tracks-btn"
                                            (click)="saveTracks(card)"
                                        >
                                            {{ labels.SAVE_TRACKS }}
                                        </button>
                                    }
                                    <button
                                        type="button"
                                        class="trail-catalog__action-btn trail-catalog__action-btn--danger"
                                        data-id="drop-btn"
                                        (click)="dropPathway(card)"
                                    >
                                        {{ labels.DROP_DECLARATION }}
                                    </button>
                                }
                                @if (actionErrorDefnId() === card.defnId && actionError()) {
                                    <p class="trail-catalog__inline-error">{{ actionError() }}</p>
                                }
                            </div>
                        </article>
                    } @empty {
                        <p class="trail-catalog__empty">No pathways found.</p>
                    }
                </div>
            }
        </section>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .trail-catalog__header {
                margin-bottom: 1rem;
            }
            .trail-catalog__title {
                margin: 0 0 0.5rem;
            }
            .trail-catalog__cohorts {
                display: flex;
                gap: 0.5rem;
                flex-wrap: wrap;
            }
            .trail-catalog__cohort-badge {
                background: var(--cn-navy-100, #e8eaf0);
                color: var(--cn-navy, #1e3a5f);
                padding: 0.2rem 0.6rem;
                border-radius: 4px;
                font-size: 0.82rem;
                font-weight: 600;
            }
            .trail-catalog__filters {
                display: flex;
                gap: 1rem;
                align-items: center;
                margin-bottom: 1rem;
                flex-wrap: wrap;
            }
            .trail-catalog__filter-toggles {
                display: flex;
                gap: 0.25rem;
            }
            .trail-catalog__filter-btn {
                padding: 0.4rem 0.8rem;
                border: 1px solid var(--color--border);
                border-radius: 4px;
                background: var(--color--surface-2-bg);
                cursor: pointer;
                font-size: 0.9rem;
            }
            .trail-catalog__filter-btn.active {
                background: var(--cn-navy, #1e3a5f);
                color: #fff;
                border-color: var(--cn-navy, #1e3a5f);
            }
            .trail-catalog__search {
                padding: 0.4rem 0.6rem;
                border: 1px solid var(--color--border);
                border-radius: 4px;
                font-size: 0.9rem;
                min-width: 180px;
            }
            .trail-catalog__error,
            .trail-catalog__inline-error {
                color: var(--color--danger);
            }
            .trail-catalog__inline-error {
                margin: 0.35rem 0 0;
                font-size: 0.82rem;
            }
            .trail-catalog__grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                gap: 1rem;
            }
            .trail-catalog__card {
                border: 1px solid var(--color--border);
                border-radius: 8px;
                padding: 1rem;
                cursor: pointer;
                transition: box-shadow 0.15s ease;
                background: var(--color--surface-2-bg);
            }
            .trail-catalog__card:hover {
                box-shadow: var(--shadow--dropdown);
            }
            .trail-catalog__card--unavailable {
                opacity: 0.7;
            }
            .trail-catalog__card-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 0.5rem;
            }
            .trail-catalog__card-name {
                margin: 0;
                font-size: 1rem;
                font-weight: 600;
            }
            .trail-catalog__card-badges {
                display: flex;
                flex-wrap: wrap;
                justify-content: flex-end;
                gap: 0.35rem;
            }
            .trail-catalog__declared-badge,
            .trail-catalog__awarded-badge {
                color: #fff;
                padding: 0.15rem 0.5rem;
                border-radius: 4px;
                font-size: 0.75rem;
                font-weight: 600;
                white-space: nowrap;
            }
            .trail-catalog__declared-badge {
                background: var(--cn-navy, #1e3a5f);
            }
            .trail-catalog__awarded-badge {
                background: var(--color--text-success, #2b7c42);
            }
            .trail-catalog__award-label {
                display: inline-block;
                margin-top: 0.4rem;
                font-size: 0.85rem;
                color: var(--color--text-muted);
            }
            .trail-catalog__card-description {
                margin: 0.4rem 0 0;
                font-size: 0.85rem;
                color: var(--color--text-muted);
                line-height: 1.4;
            }
            .trail-catalog__tracks {
                list-style: none;
                margin: 0.75rem 0 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
            .trail-catalog__track-label {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                cursor: pointer;
            }
            .trail-catalog__track-name {
                font-weight: 600;
                font-size: 0.9rem;
            }
            .trail-catalog__track-decoration,
            .trail-catalog__track-exclusivity {
                margin: 0.15rem 0 0;
                font-size: 0.82rem;
                color: var(--color--text-muted, #555);
            }
            .trail-catalog__ineligibility {
                margin: 0.5rem 0 0;
                padding-left: 1.2rem;
                font-size: 0.82rem;
                color: var(--color--danger);
            }
            .trail-catalog__actions {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
                margin-top: 0.75rem;
            }
            .trail-catalog__action-btn {
                padding: 0.35rem 0.7rem;
                border: 1px solid var(--cn-navy, #1e3a5f);
                border-radius: 4px;
                background: var(--cn-navy, #1e3a5f);
                color: #fff;
                font-size: 0.82rem;
                cursor: pointer;
            }
            .trail-catalog__action-btn:disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
            .trail-catalog__action-btn--danger {
                background: transparent;
                color: var(--color--danger, #b00020);
                border-color: var(--color--danger, #b00020);
            }
            .trail-catalog__empty {
                color: var(--color--text-muted);
                font-style: italic;
                grid-column: 1 / -1;
            }
        `,
    ],
})
export class TrailCatalogComponent {
    private static readonly SEARCH_DEBOUNCE_MS = 400;

    readonly labels = TrailLabels;

    readonly filterMode = signal<FilterMode>("available");
    readonly searchQuery = signal("");
    readonly actionError = signal<string | undefined>(undefined);
    readonly actionErrorDefnId = signal<number | undefined>(undefined);

    readonly reloadToken = input(0);
    readonly cardSelected = output<number>();
    readonly pathwaysChanged = output<void>();

    private readonly debouncedSearchQuery = signal("");
    private readonly debounceSearchQuery = effect((onCleanup) => {
        const query = this.searchQuery();
        const timeoutId = setTimeout(
            () => this.debouncedSearchQuery.set(query),
            TrailCatalogComponent.SEARCH_DEBOUNCE_MS,
        );
        onCleanup(() => clearTimeout(timeoutId));
    });

    readonly catalogRes = s25Resource({
        params: () => ({
            availableOnly: this.filterMode() === "available",
            declaredOnly: this.filterMode() === "pathways",
            q: this.debouncedSearchQuery() || undefined,
            reload: this.reloadToken(),
        }),
        loader: ({ params }) =>
            TrailApi.getCatalog({
                availableOnly: params.availableOnly,
                declaredOnly: params.declaredOnly,
                q: params.q,
            }),
    });

    readonly draftTrackIdsByDefnId = linkedSignal(() => {
        const byDefnId = new Map<number, number[]>();
        for (const card of this.catalogRes.value()?.cards ?? []) {
            byDefnId.set(card.defnId, [...(card.declaredTrackIds ?? [])]);
        }
        return byDefnId;
    });

    readonly cohortLabels = computed((): string[] => {
        const cards = this.catalogRes.value()?.cards ?? [];
        const seen = new Set<string>();
        for (const card of cards) {
            if (card.cohorts) {
                for (const cohort of card.cohorts) {
                    const label = `${cohort.cohortTypeCode}: ${cohort.cohortCode}`;
                    seen.add(label);
                }
            }
        }
        return [...seen];
    });

    readonly filteredCards = computed((): CatalogCard[] => {
        const response = this.catalogRes.value();
        if (!response?.cards) return [];
        const query = this.searchQuery().toLowerCase().trim();
        const cards = query
            ? response.cards.filter(
                  (card) =>
                      card.displayName.toLowerCase().includes(query) ||
                      (card.endingAwardDisplayName ?? "").toLowerCase().includes(query),
              )
            : [...response.cards];
        return cards.sort((cardA, cardB) => {
            const orderA = cardA.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            const orderB = cardB.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) return orderA - orderB;
            return (cardA.displayName ?? "").localeCompare(cardB.displayName ?? "", undefined, { sensitivity: "base" });
        });
    });

    setFilterMode(mode: FilterMode): void {
        this.filterMode.set(mode);
    }

    selectCard(card: CatalogCard): void {
        this.cardSelected.emit(card.defnId);
    }

    isTrackSelected(card: CatalogCard, trackId: number): boolean {
        return (this.draftTrackIdsByDefnId().get(card.defnId) ?? []).includes(trackId);
    }

    isTrackDisabled(card: CatalogCard, track: CatalogTrack): boolean {
        if (this.isTrackSelected(card, track.trackId)) return false;
        const selectedIds = this.draftTrackIdsByDefnId().get(card.defnId) ?? [];
        const exclusiveIds = (track.cannotCombineWith ?? []).map((item) => Number(item.itemId));
        return exclusiveIds.some((exclusiveId) => selectedIds.includes(exclusiveId));
    }

    cannotCombineWithLabel(track: CatalogTrack): string {
        return (track.cannotCombineWith ?? [])
            .map((item) => item.itemName)
            .filter((itemName) => !!itemName)
            .join(", ");
    }

    toggleTrack(card: CatalogCard, trackId: number): void {
        const track = card.tracks?.find((candidate) => candidate.trackId === trackId);
        if (track && this.isTrackDisabled(card, track)) return;
        this.draftTrackIdsByDefnId.update((current) => {
            const next = new Map(current);
            const selected = new Set(next.get(card.defnId) ?? []);
            if (selected.has(trackId)) selected.delete(trackId);
            else selected.add(trackId);
            next.set(card.defnId, [...selected]);
            return next;
        });
    }

    async declarePathway(card: CatalogCard): Promise<void> {
        this.actionError.set(undefined);
        this.actionErrorDefnId.set(undefined);
        try {
            await TrailApi.declare(card.defnId, this.draftTrackIdsByDefnId().get(card.defnId) ?? []);
            await this.reloadCatalog();
        } catch (caughtError) {
            await this.showActionError(card.defnId, caughtError);
        }
    }

    async saveTracks(card: CatalogCard): Promise<void> {
        const nextIds = this.draftTrackIdsByDefnId().get(card.defnId) ?? [];
        const currentIds = card.declaredTrackIds ?? [];
        const removing = currentIds.filter((trackId) => !nextIds.includes(trackId));
        if (removing.length) {
            const confirmed = await AlertService.confirm(
                `Remove ${removing.length === 1 ? "this track" : "these tracks"} from the declaration? Earned credit is kept.`,
            );
            if (!confirmed) return;
        }
        this.actionError.set(undefined);
        this.actionErrorDefnId.set(undefined);
        try {
            await TrailApi.replaceTracks(card.defnId, nextIds);
            await this.reloadCatalog();
        } catch (caughtError) {
            await this.showActionError(card.defnId, caughtError);
        }
    }

    async dropPathway(card: CatalogCard): Promise<void> {
        const confirmed = await AlertService.confirm(
            `Drop this ${TrailLabels.CURRICULUM}? Earned credit is kept, and you can declare a different pathway for this ending award.`,
        );
        if (!confirmed) return;
        this.actionError.set(undefined);
        this.actionErrorDefnId.set(undefined);
        try {
            await TrailApi.dropDeclaration(card.defnId);
            await this.reloadCatalog();
        } catch (caughtError) {
            await this.showActionError(card.defnId, caughtError);
        }
    }

    private async reloadCatalog(): Promise<void> {
        await this.catalogRes.reloadAndAwait();
        this.pathwaysChanged.emit();
    }

    private async showActionError(defnId: number, caughtError: unknown): Promise<void> {
        const message = S25Util.errorText(caughtError);
        this.actionError.set(message);
        this.actionErrorDefnId.set(defnId);
        await AlertService.alert(message);
    }
}
